<!--
 * @Description: 侧边导航栏
 * @Author: Xiaochun liu
 * @Date: 2023-10-19 20:08:31
 * @LastEditors: Lxc 1533761120@qq.com
 * @LastEditTime: 2025-07-30 10:31:33
-->
<script setup lang="tsx">
import type { MenuInst, MenuOption } from 'naive-ui'
import { SIDEBAR_INACTIVE_ROUTE } from '../helpers/constant'
import { createNavList } from './asideMenu'
import type { NavItem } from './asideMenu'
import useTokenLoginSML from '@/hooks/useTokenLoginSML'
import { useAppStore, useWorkspaceStore } from '@/store'
import { getAccessToken } from '@/api'
import { useLoginData } from '@/hooks/useLoginData'
import { setDomainToken } from '@/utils/domainToken'
import emitter, { events } from '@/event-bus'

const apiBaseUrl = import.meta.env.VITE_APP_API_BASE_URL

const route = useRoute()
const router = useRouter()

const workspaceStore = useWorkspaceStore()
const appStore = useAppStore()
const isPersonal = computed(() => workspaceStore.isPersonal)

const { isSmlUser, jumpToSml } = useTokenLoginSML()

const navList = computed(
  () => createNavList({
    enterpriseTypes: workspaceStore.enterpriseTypes,
    isPersonal: isPersonal.value,
    hasMarketAgentPermission: workspaceStore.hasMarketAgentPermission,
  }) as any,
)

// 为菜单组件转换选项，使用 menuKey 作为唯一标识
function transformMenuOptions(list: NavItem[]): any[] {
  return list.map(item => {
    const menuItem = {
      ...item,
      key: item.menuKey || item.key, // 使用 menuKey 作为菜单显示的 key
      _originalItem: item, // 保存原始项的引用
    }
    if (item.children) {
      menuItem.children = transformMenuOptions(item.children)
    }
    return menuItem
  })
}

const menuOptions = computed(() => transformMenuOptions(navList.value))

const menuInstRef = ref<MenuInst | null>(null)
const selectedKey = ref('')

function findActiveNode(tree: NavItem[], activeName: string): NavItem | null {
  let result: NavItem | null = null

  function traverse(node: NavItem) {
    if (node.activeName.includes(activeName)) {
      result = node
      return true // Stop the traversal once we find the node
    }
    if (node.children) {
      for (const child of node.children) {
        if (traverse(child)) {
          return true // Stop the traversal once we find the node
        }
      }
    }
    return false
  }

  for (const node of tree) {
    if (traverse(node)) {
      break // Stop the outer loop once we find the node
    }
  }

  return result
}
const activeIndex = computed(() =>
  findActiveNode(navList.value, route.name as string),
)
watch(
  activeIndex,
  () => {
    // 使用 menuKey 来控制菜单高亮，如果没有 menuKey 则使用 key
    selectedKey.value = (activeIndex.value?.menuKey || activeIndex.value?.key) as string
  },
  { immediate: true },
)

const { init, getSystem } = useLoginData()

const jumpToMeiye = async (item: NavItem) => {
  await init()
  function openDialog(data) {
    // 触发 AppSystemReneval 组件显示弹窗
    emitter.emit(events.showSystemRenewalDialog, {
      code: data.navData?.key,
      name: data.navData?.label,
      title: data.navData?.label,
      errorType: data.errorType,
      moreData: data.moreData,
      key: data.key,
      verifyGeography: data.verifyGeography,
    })
  }
  getSystem(item.key).then((res: any) => {
    // 判断是跳转还是打开弹窗
    if (res.stage === 0) {
      getAccessToken({ sysCode: item.key }).then((resp: any) => {
        if (['amz', 'amzen'].includes(item.key)) {
          // 亚马逊是外部系��，获取redirectUrl跳转
          window.open(resp.redirectUrl)
          setDomainToken(resp.homeToken)
        } else {
          const specialKey = item.key
          const isDev = !!apiBaseUrl.match(/work-test/)
          /** 美业数据大脑 */
          if (specialKey === 'meiye-v2') {
            const host = isDev ? 'https://datatest.meiyeyanjiu.com' : 'https://data.meiyeyanjiu.com'
            window.open(`${host}/#/auth?t=${resp.token}`, '_blank')
          }

          /** 全球新品洞察 */
          if (specialKey === 'np') {
            const host = isDev ? 'https://nptest.meiyeyanjiu.com' : 'https://np.meiyeyanjiu.com'
            window.open(`${host}/auth?t=${resp.token}`, '_blank')
          }

          /** 宣称趋势洞察 */
          if (specialKey === 'ct') {
            const host = isDev ? 'https://ct-test.meiyeyanjiu.com' : 'https://ct.meiyeyanjiu.com'
            window.open(`${host}/auth?t=${resp.token}`, '_blank')
          }

          /** 报告洞察 */
          if (specialKey === 'rpt') {
            const host = isDev ? 'https://rptest.meiyeyanjiu.com' : 'https://rpt.meiyeyanjiu.com'
            window.open(`${host}/auth?t=${resp.token}`, '_blank')
          }

          /** 数选供应链 */
          if (specialKey === 'dssc') {
            const host = isDev ? 'https://dscmtest.meiyeyanjiu.com/' : 'https://dscm.meiyeyanjiu.com/'
            window.open(`${host}/#/auth?t=${resp.token}&next=${item.outerLink}`, '_blank')
          }


          setDomainToken(resp.token)
        }
      }).catch((error) => {
        // 打开弹窗
        openDialog({ navData: item, errorType: error.resultCode })
      })
    } else if (res.stage === 1) {
      // 需校验地理位置
      openDialog({ navData: item, key: res.key, verifyGeography: true })
    }
  }).catch((error) => {
    console.log('error-----', error)
    openDialog({ navData: item, errorType: error.resultCode, moreData: error.moreData })

  })
}

function onUpdatedValue(key: string, item: any) {
  // 获取原始菜单项（如果存在 _originalItem 则使用它，否则 item 本身就是原始数据）
  // meiye-v2: 美业数据大脑
  // ct 宣称趋势洞察
  // np: 全球新品洞察
  // rpt: 报告洞察

  const originalItem = item._originalItem || item

  // 判断点击的菜单是否属于 canOpenPath 菜单或其子菜单
  const isClickingCanOpenPathMenu = () => {
    // 查找是否有父级菜单带有 canOpenPath
    function findParentWithCanOpenPath(list: NavItem[], targetKey: string): boolean {
      for (const menuItem of list) {
        // 如果当前项就是目标项，检查是否有 canOpenPath
        if ((menuItem.menuKey || menuItem.key) === targetKey || menuItem.key === originalItem.key) {
          return menuItem.canOpenPath === true
        }
        // 如果当前项有 canOpenPath，检查子菜单是否包含目标项
        if (menuItem.canOpenPath && menuItem.children) {
          for (const child of menuItem.children) {
            if ((child.menuKey || child.key) === targetKey || child.key === originalItem.key) {
              return true
            }
          }
        }
        // 递归查找子菜单
        if (menuItem.children && findParentWithCanOpenPath(menuItem.children, targetKey)) {
          return true
        }
      }
      return false
    }
    return findParentWithCanOpenPath(navList.value, key)
  }

  // 如果点击的不是 canOpenPath 菜单，立即移除特殊 class
  if (!isClickingCanOpenPathMenu()) {
    isCanOpenPathMenu.value = false
  }

  const specialKeys = ['meiye-v2', 'np', 'ct', 'rpt', 'dssc', 'npen', 'amz', 'amzen']
  const isSpecialKey = specialKeys.includes(originalItem.key)
  if (originalItem.key === 'tkradar' && isSmlUser) {
    jumpToSml()
  } else if (isSpecialKey) {
    jumpToMeiye(originalItem)
    // window.open(item.path as string);
  } else if (originalItem.outerLink?.startsWith('http')) {
    window.open(originalItem.outerLink, '_blank')
  } else if (originalItem.path) {
    selectedKey.value = key
    if (originalItem.path !== route.path) {
      router.push(originalItem.path)
    }
  }
}
function renderMenuIcon(option: MenuOption) {
  return <div class={['menu-icon', `menu-icon-${option.level}`, option.iconClassName || '']}> </div>
}

function renderMenuLabel(option: MenuOption) {
  const hasChildren = option.children && option.children.length > 0
  return <div class={['menu-label', `menu-label-${option.level}`, hasChildren ? 'menu-label-has-children' : '']}><span class="menu-label-text">{option.label}</span>{option.disabled ? <span class="menu-label-disabled"><i class="icon-menu-lock" /></span> : ''}{option.isBeta ? <span class="menu-label-beta"></span> : ''}</div>
}

function getExpandedKeys(tree: NavItem[]) {
  const expandedKeys: string[] = []

  function traverse(node: NavItem) {
    if (node.expand === true) {
      // 使用 menuKey || key 作为展开的键值
      expandedKeys.push(node.menuKey || node.key)
    }
    if (node.children) {
      node.children.forEach(child => traverse(child))
    }
  }

  tree.forEach(node => traverse(node))

  return expandedKeys
}

// 查找包含当前激活菜单的一级父菜单
function findParentMenuKey(tree: NavItem[], activeName: string): string | null {
  for (const node of tree) {
    // 如果当前节点包含激活的路由
    if (node.activeName.includes(activeName)) {
      // 如果这是一级菜单且有子菜单，返回它的 key
      if (node.level === 1 && node.children && node.children.length > 0) {
        return node.menuKey || node.key
      }
      return null
    }
    // 如果子节点包含激活的路由
    if (node.children) {
      for (const child of node.children) {
        if (child.activeName.includes(activeName)) {
          // 返回一级父菜单的 key
          if (node.level === 1) {
            return node.menuKey || node.key
          }
        }
        // 递归查找更深层级
        if (child.children) {
          const found = findParentMenuKey([child], activeName)
          if (found) {
            return node.level === 1 ? (node.menuKey || node.key) : found
          }
        }
      }
    }
  }
  return null
}

// 获取初始展开的菜单 keys（包括默认展开的和当前路由对应的一级菜单）
function getInitialExpandedKeys(): string[] {
  const defaultKeys = getExpandedKeys(navList.value)
  const currentRouteParent = findParentMenuKey(navList.value, route.name as string)

  if (currentRouteParent && !defaultKeys.includes(currentRouteParent)) {
    return [...defaultKeys, currentRouteParent]
  }

  return defaultKeys
}

// 默认展开
const isExpanded = ref(true)
const collapsed = computed(() => appStore.isSidebarCollapsed)
watch(collapsed, (newVal) => {
  if (newVal) {
    isExpanded.value = false
  } else {
    setTimeout(() => {
      isExpanded.value = true
    }, 300)
  }
})

// 当前展开的菜单 keys
const expandedKeys = ref<string[]>(getInitialExpandedKeys())

// 监听路由变化，自动展开对应的一级菜单
watch(
  () => route.name,
  (newRouteName) => {
    if (newRouteName) {
      const parentKey = findParentMenuKey(navList.value, newRouteName as string)
      if (parentKey && !expandedKeys.value.includes(parentKey)) {
        expandedKeys.value = [...expandedKeys.value, parentKey]
      }
    }
  },
)

// 处理菜单展开/收起事件
function handleExpandedKeysChange(keys: string[]) {
  const oldKeys = expandedKeys.value
  const newKeys = keys

  // 查找菜单项的辅助函数
  const findMenuItem = (list: NavItem[], targetKey: string): NavItem | null => {
    for (const item of list) {
      if ((item.menuKey || item.key) === targetKey) {
        return item
      }
      if (item.children) {
        const found = findMenuItem(item.children, targetKey)
        if (found) return found
      }
    }
    return null
  }

  // 找出新增的 key（即刚刚被展开的菜单）
  const expandedKey = newKeys.find(key => !oldKeys.includes(key))

  // 找出被移除的 key（即刚刚被收起的菜单）
  const collapsedKey = oldKeys.find(key => !newKeys.includes(key))

  if (expandedKey) {
    // 菜单被展开，找到对应的菜单项并跳转到指定页面
    const menuItem = findMenuItem(navList.value, expandedKey)
    // 如果找到菜单项且有路径，则跳转
    if (menuItem?.canOpenPath && menuItem.path !== route.path) {
      router.push(menuItem.path)
      // 同时更新选中的菜单项，使父级菜单高亮
      selectedKey.value = expandedKey
    }
  } else if (collapsedKey) {
    // 菜单被收起
    const menuItem = findMenuItem(navList.value, collapsedKey)
    if (menuItem?.canOpenPath) {
      // 如果当前路由和菜单路由一致，允许正常收起
      if (menuItem.path === route.path) {
        // 正常收起，继续执行后面的更新操作
      } else {
        // 如果是 canOpenPath 菜单且路由不一致，不允许收起，保持展开状态并跳转
        expandedKeys.value = [...keys, collapsedKey]
        router.push(menuItem.path)
        // 同时更新选中的菜单项，使父级菜单高亮
        selectedKey.value = collapsedKey
        return // 直接返回，不执行后面的更新操作
      }
    }
  }

  // 更新展开的 keys
  expandedKeys.value = keys
}

// 检查当前路由是否属于 canOpenPath = true 的菜单
const isCanOpenPathMenu = ref(false)

// 监听路由变化，控制特殊 class 的添加和移除
watch(
  () => route.name,
  (newRouteName) => {
    // 路由切换时，先移除特殊 class
    isCanOpenPathMenu.value = false

    // 在下一个 tick 判断新路由是否需要特殊 class
    nextTick(() => {
      const currentRouteName = newRouteName as string

      // 递归查找菜单项
      function findMenuWithCanOpenPath(list: NavItem[]): boolean {
        for (const item of list) {
          // 如果当前菜单项有 canOpenPath 属性且包含当前路由
          if (item.canOpenPath && item.activeName.includes(currentRouteName)) {
            return true
          }
          // 如果当前菜单项有 canOpenPath 属性且当前路由在其子菜单中
          if (item.canOpenPath && item.children) {
            for (const child of item.children) {
              if (child.activeName.includes(currentRouteName)) {
                return true
              }
            }
          }
          // 递归查找子菜单
          if (item.children && findMenuWithCanOpenPath(item.children)) {
            return true
          }
        }
        return false
      }

      isCanOpenPathMenu.value = findMenuWithCanOpenPath(navList.value)
    })
  },
  { immediate: true }
)
</script>

<template>
  <div v-if="!SIDEBAR_INACTIVE_ROUTE.includes(route.name as string)" class="app-left-aside-wrapper"
    :class="{ collapsed }">
    <div class="sidebar-header" @click="router.push('/')">
      <div v-show="collapsed" class="logo" />
      <div v-show="!collapsed" class="logo-new" />
    </div>

    <div class="app-left-aside" :class="{ 'has-can-open-path': isCanOpenPathMenu }">
      <NScrollbar style="max-height: 100%;">
        <div class="app-left-aside-content">
          <n-menu ref="menuInstRef" v-model:value="selectedKey" v-model:expanded-keys="expandedKeys"
            :collapsed="collapsed" :collapsed-width="66" :options="menuOptions" :render-icon="renderMenuIcon"
            :render-label="renderMenuLabel" :dropdown-props="{
              renderLabel: renderMenuLabel,
              // trigger: 'click',
              // duration: 300000,
              // @ts-ignore
              class: 'app-sidebar-menu-dropdown',
            }" :indent="8" :on-update:value="onUpdatedValue" :on-update:expanded-keys="handleExpandedKeysChange" />
        </div>
      </NScrollbar>
    </div>

    <!-- <a class="full-process" :href="`/introdution/product-process-solution?workspace=${route.query.workspace}`"
      target="_blank" /> -->
    <div class="sidebar-footer">
      <div v-show="!collapsed" class="fold-btn" @click="appStore.toggleSidebarCollapsed">
        <i class="fold-icon" />
        <span v-show="isExpanded">收起侧边栏</span>
      </div>
      <div v-show="collapsed" class="expand-btn" @click="appStore.toggleSidebarCollapsed" />
    </div>
  </div>
</template>

<style lang="scss">
$menu-active-bg: linear-gradient(90deg, #FF67D5 0%, #E39CFF 100%);
$menu-hover-bg: #F0F4FF;

@mixin menu-icons {
  .icon-home {
    background-image: url("./icons/icon-home.png");
  }

  .icon-ai {
    background-image: url("./icons/icon-ai.png");
  }

  .icon-create {
    background-image: url("./icons/icon-create.png");
  }

  .icon-manage {
    background-image: url("./icons/icon-manage.png");
  }

  .icon-knowledge {
    background-image: url("./icons/icon-knowledge.png");
  }

  .icon-inspiration {
    background-image: url("./icons/icon-inspiration.png");
  }

  .icon-sand {
    background-image: url("./icons/icon-sand.png");
  }

  .icon-insight {
    background-image: url("./icons/icon-insight.png");
  }

  .icon-resource {
    background-image: url("./icons/icon-resource.png");
  }

  .icon-digital {
    background-image: url("./icons/icon-digital.png");
  }

  .icon-inspiration-library {
    background-image: url("./icons/icon-inspiration-library.png");
  }

  .icon-market-agent {
    background-image: url("./icons/icon-market-agent.png");
  }

  .icon-chain {
    background-image: url("./icons/icon-chain.png");
  }

  .icon-mcp {
    background-image: url("./icons/icon-mcp.png");
  }
}

@mixin menu-active-icons {
  .icon-home {
    background-image: url("./icons/icon-home-active.png");

  }

  .icon-create {
    background-image: url("./icons/icon-create-active.png");

  }

  .icon-ai {
    background-image: url("./icons/icon-ai-active.png");
  }

  .icon-manage {
    background-image: url("./icons/icon-manage-active.png");

  }

  .icon-knowledge {
    background-image: url("./icons/icon-knowledge-active.png");

  }

  .icon-inspiration {
    background-image: url("./icons/icon-inspiration-active.png");

  }

  .icon-sand {
    background-image: url("./icons/icon-sand-active.png");

  }

  .icon-insight {
    background-image: url("./icons/icon-insight-active.png");

  }

  .icon-resource {
    background-image: url("./icons/icon-resource-active.png");

  }

  .icon-digital {
    background-image: url("./icons/icon-digital-active.png");

  }

  .icon-inspiration-library {
    background-image: url("./icons/icon-inspiration-library-active.png");

  }

  .icon-market-agent {
    background-image: url("./icons/icon-market-agent-active.png");
  }

  .icon-chain {
    background-image: url("./icons/icon-chain-active.png");
  }

  .icon-mcp {
    background-image: url("./icons/icon-mcp-active.png");
  }
}

.app-sidebar-menu-dropdown {

  .menu-label-1 {
    font-weight: 600;
  }

  .menu-label-3 {
    font-size: 12px;
  }

  .menu-label-beta {
    position: absolute;
    right: 5px;
    top: 1px;
    width: 30px;
    height: 12px;
    background-size: cover;
    background-image: url("../icons/beta.png");
  }

  .icon-menu-lock {
    position: relative;
    top: 1px;
    margin-left: 6px;
    display: inline-block;
    width: 12px;
    height: 12px;
    background-size: cover;
    background-image: url("../icons/menu-lock.png");
  }

  .n-dropdown-option {
    min-width: 160px;

    &:not(:last-child) {
      margin-bottom: 8px;
    }
  }

  // --n-option-icon-prefix-width: 32px;
  // 弹出菜单默认样式
  .n-dropdown-option .n-dropdown-option-body .n-dropdown-option-body__prefix.n-dropdown-option-body__prefix--show-icon {
    width: 14px !important;
  }

  .n-dropdown-option .n-dropdown-option-body:not(.n-dropdown-option-body--disabled).n-dropdown-option-body--pending::before {
    background: $menu-hover-bg !important;
  }

  .n-dropdown-option .n-dropdown-option-body:not(.n-dropdown-option-body--disabled).n-dropdown-option-body--active {
    color: #fff !important;

    &::before {
      background: $menu-active-bg !important;
      border-radius: 8px;
    }
  }
}

.app-left-aside-wrapper {
  position: relative;
  z-index: 1001;
  width: 180px;
  height: 100%;
  background: #ffffff;
  box-shadow: 4px 0px 12px 0px rgba(215, 231, 255, 0.5);
  transition: width 0.3s ease-in-out;
  // overflow: auto;

  .sidebar-header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 46px;
    cursor: pointer;

    .logo {
      flex-shrink: 0;
      width: 42px;
      height: 42px;
      background-size: cover;
      background-image: url("./icons/logo.png");
    }

    .logo-new {
      flex-shrink: 0;
      width: 140px;
      height: 48px;
      background-size: cover;
      background-image: url("./icons/logo-new-2.png");
    }

    // .title {
    //   margin-left: 8px;
    //   width: 76px;
    //   height: 22px;
    //   font-weight: 500;
    //   font-size: 16px;
    //   line-height: 19px;
    //   text-align: center;
    //   background: linear-gradient(1180deg, #FF5093 39%, #A088FF 100%);
    //   -webkit-background-clip: text;
    //   background-clip: text;
    //   color: transparent;
    // }

  }

  .full-process {
    position: absolute;
    bottom: 12px;
    left: 50%;
    transform: translateX(-50%);
    width: 162px;
    height: 55px;
    background-size: cover;
    background-image: url("./icons/full-process.png");
    border: 1px solid transparent;
    border-radius: 4px;
    overflow: hidden;
    background-repeat: no-repeat;

    &:hover {
      border: 1px solid #4050ff;
    }
  }

  &.collapsed {
    width: 66px;

    .full-process {
      display: none;
    }

    .app-left-aside {
      .n-menu {
        .n-menu-item-content {
          padding-left: 12px !important;
        }
      }
    }
  }

  .app-left-aside {
    height: calc(100% - 46px - 32px);

    .app-left-aside-content {
      padding: 0 8px;
    }

    .menu-label-1 {
      font-weight: 600;
    }

    .menu-label-3 {
      font-size: 12px;
    }

    // .menu-label-2 {
    //   &.menu-label-has-children {
    //     color: #242F57;
    //     font-weight: 600;
    //   }
    // }

    .menu-label-beta {
      position: absolute;
      right: -20px;
      top: -10px;
      width: 30px;
      height: 12px;
      background-size: cover;
      background-image: url("../icons/beta.png");
    }

    .icon-menu-lock {
      position: relative;
      top: 1px;
      margin-left: 6px;
      display: inline-block;
      width: 12px;
      height: 12px;
      background-size: cover;
      background-image: url("../icons/menu-lock.png");
    }

    .n-menu {

      .n-menu-item {
        height: 38px;
        margin-top: 2px;
      }

      .n-submenu-children {
        .n-menu-item {
          margin-top: 0;
        }
      }

      .n-menu-item + .n-submenu,
      .n-submenu + .n-submenu {
        margin-top: 6px;
      }


      .n-menu-item-content {
        // padding-left: 17px !important;
        height: 38px;
        padding-right: 10px;

        &::before {
          background: #ffffff;
          transition: none !important;
        }

        // &.n-menu-item-content--disabled {
        //   opacity: 1;
        //   .n-menu-item-content__icon {
        //     opacity: 0.45;
        //   }
        //   .menu-label-text {
        //     opacity: 0.45;
        //   }

        // }

        &:not(.n-menu-item-content--disabled):hover {
          &::before {
            background: $menu-hover-bg;
          }
        }

        &:not(.n-menu-item-content--disabled).n-menu-item-content--child-active:hover .n-menu-item-content-header {
          color: #242f57;
        }

        &.n-menu-item-content--selected:hover {
          .n-menu-item-content-header {
            color: #fff;
          }
        }

        .n-menu-item-content__icon {
          position: relative;

          .menu-icon {
            width: 20px;
            height: 20px;
            background-size: cover;

          }

          @include menu-icons;
        }

        .n-menu-item-content-header {
          position: relative;
          left: -8px;
          font-size: 13px;
          color: #242f57;
          line-height: 20px;
          transition: none;
          overflow: visible;

          &:hover {
            color: #242f57;
          }
        }

        .n-menu-item-content__arrow {
          font-size: 12px;
          color: #425466;
        }

        &::before {
          left: 0;
          right: 0;
          border-radius: 8px;
        }
      }

      .n-menu-item-content:not(.n-menu-item-content--disabled):not(.n-menu-item-content--child-active).n-menu-item-content--hover::before {
        background: $menu-hover-bg !important;
      }

      .n-menu-item-content--selected {
        .n-menu-item-content-header {
          color: #fff !important;
        }

        .n-menu-item-content__icon {
          @include menu-active-icons;
        }

        &::before {
          background: $menu-active-bg;
        }
      }

      .n-menu-item-content:not(.n-menu-item-content--disabled):not(.n-menu-item-content--child-active):hover {
        &::before {
          background: $menu-hover-bg;
        }

      }

      .n-menu-item-content:not(.n-menu-item-content--disabled).n-menu-item-content--selected:hover::before {
        background: $menu-active-bg;
      }

      .n-submenu {
        margin-top: 0;
      }

      .n-submenu {
        &>.n-submenu-children {
          &>.n-submenu {
            &>.n-submenu-children {
              .n-menu-item-content {
                padding-left: 32px !important;

                .n-menu-item-content__icon {
                  margin-right: 4px !important;
                }
              }
            }
          }
        }
      }

      // 处理缩进
      .n-submenu-children {
        &>.n-menu-item {
          &>.n-menu-item-content {
            padding-left: 8px !important;
          }
        }

        &>.n-submenu {
          &>.n-menu-item {
            &>.n-menu-item-content {
              padding-left: 8px !important;

            }
          }
        }

        .n-menu-item-content--selected {
          .n-menu-item-content__icon {
            .menu-icon {
              background: #fff;
            }
          }
        }

        .n-menu-item-content__icon {
          // justify-content: flex-end;

          .menu-icon {
            width: 4px;
            height: 4px;
            margin-right: 6px;
            background: #8fa1b2;
            border-radius: 50%;

            &.menu-icon-2 {
              width: 22px;
              height: 22px;
              display: none;
            }

          }

        }

        .n-menu-item-content-header {
          // left: -16px;
          color: #425466;
        }
      }
    }

  }

  .sidebar-footer {
    height: 32px;
    border-top: 1px solid #E5E5E5;
    display: flex;
    align-items: center;
    justify-content: center;

    .fold-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #8FA1B2;
      font-size: 12px;
      gap: 4px;
      cursor: pointer;
      transition: all 0.3s ease-in-out;

      .fold-icon {
        width: 16px;
        height: 16px;
        background-size: cover;
        background-image: url("./icons/icon-menu-fold.png");
      }

    }

    .expand-btn {

      width: 16px;
      height: 16px;
      background-size: cover;
      background-image: url("./icons/icon-menu-expand.png");
      cursor: pointer;
    }
  }
}

// 针对 canOpenPath = true 的菜单，一级菜单被选中时高亮
.app-left-aside-wrapper {
  .app-left-aside.has-can-open-path {
    .n-menu {

      // 只对一级菜单（直接子元素）应用高亮样式
      >.n-menu-item>.n-menu-item-content,
      >.n-submenu>.n-menu-item>.n-menu-item-content {

        // 收起状态下，子菜单激活时父级高亮
        &.n-menu-item-content--collapsed.n-menu-item-content--child-active {
          &::before {
            background: $menu-active-bg !important;
          }

          @include menu-active-icons;

          .n-menu-item-content-header {
            color: #fff !important;

            &:hover {
              color: #fff !important;
            }
          }
        }

        // 展开状态下，一级菜单被选中时高亮
        &:not(.n-menu-item-content--collapsed).n-menu-item-content--child-active {
          &::before {
            background: $menu-active-bg !important;
          }

          @include menu-active-icons;

          .n-menu-item-content-header {
            color: #fff !important;

            &:hover {
              color: #fff !important;
            }
          }
        }
      }
    }
  }
}
</style>
